import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import ConfirmDialog from "../../components/ConfirmDialog";

export default function MenusAdminList() {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [editItem, setEdit]       = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [form, setForm]           = useState({ name:"", slug:"", path:"", parent_slug:"", sort_order:0 });

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/menus");
      setRows(res.data?.menus || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load menus");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEdit(null);
    setForm({ name:"", slug:"", path:"", parent_slug:"", sort_order:0 });
    setModal(true);
  };

  const openEdit = (row) => {
    setEdit(row);
    setForm({ name:row.name, slug:row.slug, path:row.path, parent_slug:row.parent_slug||"", sort_order:row.sort_order });
    setModal(true);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim() || !form.path.trim()) {
      toast.error("Name, slug and path are required");
      return;
    }
    try {
      const payload = { ...form, parent_slug: form.parent_slug || null, sort_order: Number(form.sort_order) };
      if (editItem) {
        await api.put(`/admin/menus/${editItem.menu_id}`, payload);
        toast.success("Menu updated");
      } else {
        await api.post("/admin/menus", payload);
        toast.success("Menu created");
      }
      setModal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/menus/${confirmId}`);
      toast.success("Menu deleted");
      setConfirmId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Delete failed");
    }
  };

  const topLevel = rows.filter(m => !m.parent_slug);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Menus</h1>
          <p className="text-sm text-gray-600 mt-1">Manage sidebar navigation menus</p>
        </div>
        <button
          className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90 self-start"
          onClick={openAdd}
        >
          + Add Menu
        </button>
      </div>

      {/* Table */}
      <div className="mt-6 border rounded-2xl overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 text-sm font-semibold">
          Menus List
        </div>

        {loading ? (
          <div className="p-4 text-sm text-gray-600">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">No menus found.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-t bg-white">
              <tr className="text-left">
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Path</th>
                <th className="px-4 py-3">Parent</th>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="border-t">
              {rows.map((m) => (
                <tr key={m.menu_id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-500">{m.menu_id}</td>
                  <td className="px-4 py-3 font-semibold" style={{paddingLeft: m.parent_slug ? 32 : 16}}>
                    {m.parent_slug && <span className="text-gray-400 mr-1">↳</span>}
                    {m.name}
                  </td>
                  <td className="px-4 py-3">
                    <code className="bg-gray-100 rounded px-1 py-0.5 text-xs">{m.slug}</code>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{m.path}</td>
                  <td className="px-4 py-3">
                    {m.parent_slug
                      ? <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-700">{m.parent_slug}</span>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3">{m.sort_order}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-gray-50"
                        onClick={() => openEdit(m)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="rounded-lg border px-3 py-1 text-xs font-semibold hover:bg-red-50 text-red-600 border-red-200"
                        onClick={() => setConfirmId(m.menu_id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl border w-full max-w-[480px] max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h2 className="font-bold text-lg">{editItem ? "Edit Menu" : "Add Menu"}</h2>
              <button type="button" className="text-gray-400 hover:text-gray-600 text-xl" onClick={() => setModal(false)}>×</button>
            </div>

            <form onSubmit={onSubmit} className="p-5 space-y-4">
              {[
                { label:"Name",  name:"name",  placeholder:"e.g. Bookings" },
                { label:"Slug",  name:"slug",  placeholder:"e.g. bookings" },
                { label:"Path",  name:"path",  placeholder:"e.g. /admin/bookings" },
              ].map(({ label, name, placeholder }) => (
                <div key={name}>
                  <label className="text-sm font-semibold">{label}</label>
                  <input
                    name={name}
                    value={form[name]}
                    onChange={onChange}
                    placeholder={placeholder}
                    className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm"
                  />
                </div>
              ))}

              <div>
                <label className="text-sm font-semibold">Parent Menu <span className="text-gray-400 font-normal">(optional)</span></label>
                <select
                  name="parent_slug"
                  value={form.parent_slug}
                  onChange={onChange}
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm"
                >
                  <option value="">— None (top level) —</option>
                  {topLevel
                    .filter(m => m.menu_id !== editItem?.menu_id)
                    .map(m => <option key={m.menu_id} value={m.slug}>{m.name}</option>)}
                </select>
              </div>

              <div>
                <label className="text-sm font-semibold">Sort Order</label>
                <input
                  type="number"
                  name="sort_order"
                  value={form.sort_order}
                  onChange={onChange}
                  className="mt-2 w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm"
                />
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <button
                  type="submit"
                  className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold hover:opacity-90"
                >
                  {editItem ? "Update" : "Create"}
                </button>
                <button
                  type="button"
                  className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                  onClick={() => setModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmId && (
        <ConfirmDialog
          message="Are you sure you want to delete this menu?"
          onConfirm={remove}
          onCancel={() => setConfirmId(null)}
        />
      )}
    </div>
  );
}